import { useCallback, useEffect, useState } from 'react'
import { formatDate } from 'date-fns'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { useShallow } from 'zustand/react/shallow'

import {
  PRODUCTION_REPORT_STATE,
  ProductionReportRequest,
  StreamEvent
} from '@img-arena/consoles-api-streaming'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography
} from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'
import { streamingRestApi } from '@/service/streamingApi'
import { useAuthStore } from '@/stores/useAuthStore'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps } from '@/utils/forms'
import { onFieldChange } from '@/utils/forms/formik'

import { IssueForm } from '../IssueForm'
import { EditableIssue } from '../IssueForm/types'
import { apiToEditable, editableToApi } from '../IssueForm/utils'
import { productionReportFormValidationSchema } from './productionReportForm.validation'
import { ProductionReport } from './types'

type CreateProductionReportFormProps = FormDialogProps<ProductionReport> & {
  eventId: number
  hasReportAvailable?: boolean
}

const createBaseIssue = (): EditableIssue => {
  return {
    startDate: new Date(),
    lossOfGamePlay: 0,
    problemDescription: '',
    attachment: null,
    responsibility: null,
    cases: null
  }
}

export const CreateProductionReportForm = ({
  open,
  onClose,
  onSubmit,
  eventId,
  hasReportAvailable
}: CreateProductionReportFormProps) => {
  const eventsApi = streamingRestApi.endpoints.events

  const [event, setEvent] = useState<StreamEvent | null>(null)
  const [issues, setIssues] = useState<EditableIssue[]>([])

  useEffect(() => {
    if (hasReportAvailable) return

    const ac = new AbortController()

    try {
      eventsApi
        .getEvents({
          eventId,
          signal: ac.signal
        })
        .then(({ data }) => {
          setEvent(data)
          formik.resetForm({
            values: {
              ...formik.values,
              event: data
            }
          })
        })
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: `Failed to fetch event: ${eventId}`
      })
    }

    return () => ac.abort()
  }, [eventId])

  useEffect(() => {
    if (!hasReportAvailable) {
      setIssues([createBaseIssue()])
      return
    }

    const ac = new AbortController()

    try {
      eventsApi
        .getProductionReport({
          eventId,
          signal: ac.signal
        })
        .then(({ data }) => {
          if (data.length > 1) {
            const event = data[0].event
            const issues = data[0].issues

            setEvent(event)
            setIssues(issues.map(apiToEditable))
            formik.resetForm({
              values: {
                ...formik.values,
                event,
                issues
              }
            })
            return
          }

          const issues = data[0].issues.map(apiToEditable)
          setEvent(data[0].event)
          setIssues(issues)
          formik.resetForm({
            values: {
              ...formik.values,
              event: data[0].event,
              issues: data[0].issues
            }
          })
        })
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: `Failed to fetch production report for event: ${eventId}`
      })
    }

    return () => ac.abort()
  }, [hasReportAvailable])

  const { user } = useAuthStore(
    useShallow((state) => ({
      user: state.user
    }))
  )

  const formik = useFormik<ProductionReport>({
    initialValues: {
      event,
      issues: [],
      startDate: formatDate(new Date(), 'dd/MM/yyyy hh:mm aa'),
      notes: [],
      state: PRODUCTION_REPORT_STATE.FAILED
    },
    validationSchema: productionReportFormValidationSchema,
    onSubmit: async (data) => {
      if (!data.event) return

      try {
        if (hasReportAvailable) {
          await eventsApi.postProductionReport({
            eventId,
            requestBody: {
              ...data,
              issues: [],
              state: PRODUCTION_REPORT_STATE.SUCCESS
            } as ProductionReportRequest
          })

          await new Promise((resolve) => setTimeout(resolve, 1000))
        }

        await eventsApi.postProductionReport({
          eventId,
          requestBody: data as ProductionReportRequest
        })

        onClose?.()
        onSubmit?.()
        enqueueSnackbar({
          variant: 'success',
          message: 'Report submitted successfuly.'
        })
      } catch {
        enqueueSnackbar({
          variant: 'error',
          message: 'Unable to submit the report.'
        })
      }
    }
  })

  const handleAddIssue = () => {
    setIssues((prev) => [...prev, createBaseIssue()])
  }

  const handleSaveIssue = (index: number) => (issue: EditableIssue) =>
    setIssues((prev) => {
      const issues = [...prev]
      issues[index] = issue

      onFieldChange(
        formik,
        'issues',
        issues.filter((issue) => !!issue?.responsibility).map(editableToApi)
      )

      return issues
    })

  const handleRemoveIssue = useCallback(
    (index: number) => (_e: React.MouseEvent<HTMLButtonElement>) => {
      _e.stopPropagation()
      const newIssues = [...issues]
      newIssues.splice(index, 1)
      setIssues(newIssues)

      onFieldChange(
        formik,
        'issues',
        newIssues.filter((issue) => !!issue?.responsibility).map(editableToApi)
      )
    },
    [issues, setIssues, formik]
  )

  if (!event) return

  return (
    <ConsoleDialog
      dialogSize="md"
      open={open}
      SaveButtonProps={{ ...formikSaveButtonProps(formik), text: 'Submit' }}
      title="Create Production Report"
      onClose={onClose}
      width={1200}
      fullWidth
    >
      <Grid container m={0} columnSpacing={2} rowSpacing={2} columns={12}>
        <Grid mx={2} container rowSpacing={2} size={12}>
          <Grid size={4}>
            <ConsoleTextField label="Event" value={event.title} disabled />
          </Grid>

          <Grid size={4}>
            <ConsoleTextField label="Report By" value={user?.name || ''} disabled />
          </Grid>
        </Grid>

        <Grid container spacing={2} size={12}>
          {issues.map((issue, index) => (
            <Grid key={index} size={12}>
              <Accordion defaultExpanded={index === 0 && issues.length === 1 ? true : false}>
                <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                  <Stack
                    width="100%"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body1">
                      Issue #{index + 1} {issue.responsibility ? `(${issue.responsibility})` : ''}
                    </Typography>
                    <IconButton
                      data-testid="remove-issue-button"
                      onClick={handleRemoveIssue(index)}
                      disabled={index === 0 && issues.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <IssueForm issue={issue} onSubmit={handleSaveIssue(index)} />
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
          <Grid size={12}>
            <Accordion onChange={() => false} disableGutters>
              <AccordionSummary>
                <Stack
                  width="100%"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body1">Issue</Typography>
                  <Button onClick={handleAddIssue}>Add</Button>
                </Stack>
              </AccordionSummary>
            </Accordion>
          </Grid>
        </Grid>
      </Grid>
    </ConsoleDialog>
  )
}
